import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowLeft, BookOpen, Shield, Mail, Camera, Users, MessageCircle } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const ACCENT = '#3a5a40';

const PRINCIPLES = [
  {
    icon: BookOpen,
    title: 'Editorial discovery, not paid placement',
    body: 'Properties featured on GoHimalayan are discovered, written about, and photographed by our editorial team because we believe they deserve a wider audience. There is no listing fee, no editorial fee, and no pay-to-feature model. Featuring is a curatorial decision, never a commercial one.',
  },
  {
    icon: Camera,
    title: 'A publication first',
    body: 'GoHimalayan operates the way travel publications, guidebooks, and magazines have for decades — covering hospitality through editorial criteria. The act of writing about a hotel, homestay, or experience and recommending it to readers is a long-standing form of journalism. Our coverage follows the same principles: independent assessment, honest writing, and respect for the subject.',
  },
  {
    icon: Users,
    title: 'Hosts are invited, never enrolled without consent',
    body: 'Editorial coverage on GoHimalayan does not enrol a property into any commercial program. If the host of a featured property would like to formally join the GoHimalayan Partner Program — to receive direct booking enquiries, manage their calendar, and access marketing infrastructure — that is an explicit, opt-in relationship initiated through our partner page. Featuring and partnership are separate.',
  },
  {
    icon: Shield,
    title: 'Imagery rights and attribution',
    body: 'Where editorial coverage uses imagery, we either (a) capture original photography ourselves, (b) source from properly licensed stock libraries, or (c) use imagery shared publicly by the property with attribution. If a host or photographer believes their imagery has been used incorrectly, we honour takedown requests promptly and replace the imagery without dispute.',
  },
  {
    icon: Mail,
    title: 'A simple opt-out, always honoured',
    body: 'If you are the host of a featured property and prefer not to be discovered through GoHimalayan — for any reason — write to editorial@gohimalayan.com. We will remove the editorial coverage promptly. There is no friction, no negotiation, and no questions asked. The mountains belong to the people who steward them.',
  },
];

export default function EditorialPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="editorial-policy-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Mountain className="h-7 w-7" style={{ color: ACCENT }} strokeWidth={2.5} />
            <span className="text-lg font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors text-sm" data-testid="back-home-btn">
            <ArrowLeft className="h-4 w-4" />Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: ACCENT }}>Editorial Policy</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 leading-tight mb-6" style={pf}>
            How GoHimalayan discovers, features, and writes about properties.
          </h1>
          <p className="text-stone-500 text-lg leading-relaxed" style={pf}>
            Plain-language clarity on how we operate as an editorial discovery platform — and the rights every host, photographer, and reader can rely on.
          </p>
          <p className="text-xs text-stone-400 mt-6 tracking-wide">Last updated: April 2026</p>
        </div>
      </section>

      {/* Preamble */}
      <section className="pb-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-stone-100 shadow-sm">
            <p className="text-stone-900 text-[15px] md:text-[16px] leading-[1.85] mb-4 font-semibold" style={pf}>
              No host has ever paid us. We have never taken a commission. This policy is what protects that.
            </p>
            <p className="text-stone-700 text-base md:text-[17px] leading-[1.85]" style={pf}>
              GoHimalayan is, at its heart, a publication. Our editorial team — writers, photographers, travellers, and creators who live and breathe the Himalayas — spends time on the ground discovering exceptional places to stay across the mountains. We write about them, photograph them, and place them in front of travellers searching for them. This page describes the principles behind that work, so every host, host-photographer, and reader knows exactly how we operate.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="pb-20 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 font-semibold mb-3">Five principles</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-10" style={pf}>What guides our editorial work.</h2>

          <div className="space-y-6" data-testid="principles">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 md:p-8 border border-stone-100" data-testid={`principle-${i}`}>
                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${ACCENT}10` }}>
                    <p.icon className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-1" style={{ color: ACCENT }}>Principle {String(i + 1).padStart(2, '0')}</p>
                    <h3 className="text-lg font-bold text-stone-900 mb-3" style={pf}>{p.title}</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to reach us */}
      <section className="pb-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl p-10 text-center" style={{ background: `linear-gradient(135deg, ${ACCENT}10 0%, ${ACCENT}05 100%)`, border: `1px solid ${ACCENT}20` }}>
            <Mail className="w-10 h-10 mx-auto mb-5" style={{ color: ACCENT }} strokeWidth={1.5} />
            <p className="text-[11px] tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: ACCENT }}>How to reach us</p>
            <h3 className="text-2xl font-bold text-stone-900 mb-4" style={pf}>Editorial concerns, takedowns, and corrections</h3>
            <p className="text-stone-600 text-base leading-relaxed max-w-xl mx-auto mb-6" style={pf}>
              Whether you'd like coverage removed, corrected, updated, or you'd like to begin a partnership conversation — we read every email and respond within two working days.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <a href="mailto:editorial@gohimalayan.com" className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white rounded-full text-sm font-semibold hover:bg-stone-800 transition-colors" data-testid="editorial-email">
                <Mail className="w-4 h-4" />editorial@gohimalayan.com
              </a>
              <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%20editorial%20team%20—%20" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full text-sm font-semibold hover:bg-[#1fb855] transition-colors" data-testid="editorial-whatsapp">
                <MessageCircle className="w-4 h-4" />WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-8 px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mountain className="h-5 w-5" style={{ color: ACCENT }} />
            <span className="text-sm text-stone-500">© 2026 GoHimalayan</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-stone-400">
            <Link to="/about" className="hover:text-stone-700 transition-colors">About</Link>
            <Link to="/host" className="hover:text-stone-700 transition-colors">Host with us</Link>
            <Link to="/privacy" className="hover:text-stone-700 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-700 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
